<template>
  <div class="">
    <div v-if="open" class="z-40" @click="close" />

    <div class="relative flex flex-row items-center">
      <button @click="toggle"
        class="w-8 h-8 bg-primary rounded-full shadow-lg flex items-center justify-center hover:bg-gray-50 transition-all duration-200 text-white hover:text-primary">
        <font-awesome-icon icon="user" class="" />
      </button>
    </div>

    <div v-if="open"
      class="fixed bottom-4 left-4 z-50 w-[280px] bg-white rounded-lg shadow-xl border border-gray-600 flex flex-col max-h-[80vh] overflow-y-auto">
      <div class="px-3 py-2 border-b border-gray-100 text-sm font-medium text-gray-500 flex flex-row items-center">
        <span class="flex-grow">Account</span>
        <button @click="close" class="text-gray-300 hover:text-gray-500 transition-colors p-0 w-4 h-4">
          <font-awesome-icon icon="times" class="text-xs" />
        </button>
      </div>

      <div class="flex-1">
        <button @click="goToProfile"
          class="w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 transition-colors border-b border-gray-50">
          <font-awesome-icon icon="user" class="w-4 text-gray-400" />
          <span>My Profile</span>
        </button>

        <button @click="goToNotifications"
          class="w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 transition-colors border-b border-gray-50">
          <font-awesome-icon icon="bell" class="w-4 text-gray-400" />
          <span>View Notifications</span>
        </button>

        <button @click="reportIssue"
          class="w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 transition-colors border-b border-gray-50">
          <font-awesome-icon icon="bug" class="w-4 text-gray-400" />
          <span>Report an Issue</span>
        </button>

        <!-- Admin Fake Claims Panel (Issue #183) -->
        <div v-if="isAdmin" class="border-b border-gray-100">
          <div class="px-4 py-2 bg-orange-50">
            <div class="flex items-center gap-2 text-xs font-semibold text-orange-700 mb-2">
              <font-awesome-icon icon="flask" class="w-3" />
              <span>Admin: Fake Claims (Client Only)</span>
            </div>
            <div class="text-xs text-gray-600 mb-2">
              Test external self UX without server claims. Session-local only.
            </div>
            <form class="space-y-1.5" @submit.prevent="applyFakeClaims">
              <label class="flex items-center gap-2 cursor-pointer text-xs">
                <input type="checkbox" v-model="fakeClaims.internal" class="rounded" />
                <span class="text-gray-700">Internal</span>
              </label>
              <label class="flex items-center gap-2 cursor-pointer text-xs">
                <input type="checkbox" v-model="fakeClaims.flight_test_crew" class="rounded" />
                <span class="text-gray-700">Flight Test Crew</span>
              </label>
              <label class="flex items-center gap-2 cursor-pointer text-xs">
                <input type="checkbox" v-model="fakeClaims.signatory" class="rounded" />
                <span class="text-gray-700">Signatory (STC)</span>
              </label>
              <label class="flex items-center gap-2 cursor-pointer text-xs">
                <input type="checkbox" v-model="fakeClaims.signatory_military" class="rounded" />
                <span class="text-gray-700">Signatory (Military)</span>
              </label>
              <button type="submit"
                class="w-full mt-2 px-3 py-1.5 text-xs bg-primary hover:opacity-90 text-white rounded transition-colors">
                Apply Fake Claims & Reload
              </button>
            </form>
            <button type="button" @click="clearFakeClaims"
              class="w-full mt-2 px-3 py-1.5 text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 rounded transition-colors">
              Clear All & Reload
            </button>
          </div>
        </div>

        <button @click="handleSignout"
          class="w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm text-red-600 hover:bg-red-50 transition-colors">
          <font-awesome-icon icon="sign-out-alt" class="w-4" />
          <span>Log Out</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { useStore } from "vuex";
import { useRouter } from "vue-router";
import { signout, getAdminFakeClaims, setAdminFakeClaims } from "../authState.js";
import { addBug } from "../views/Home/actions.js";

const props = defineProps({
  form: {
    type: Object,
    required: true
  }
});

const store = useStore();
const router = useRouter();

const open = ref(false);
const fakeClaims = ref({
  internal: false,
  flight_test_crew: false,
  signatory: false,
  signatory_military: false
});

// Real token claims control user loading, so fake claims cannot remove this admin access.
const isAdmin = computed(() => {
  const user = store.state.user;
  return !!user?.Security?.General?.ApplicationAdmin;
});

// Load fake claims from sessionStorage on mount
onMounted(() => {
  const stored = getAdminFakeClaims();
  if (stored) {
    fakeClaims.value = {
      internal: !!stored.internal,
      flight_test_crew: !!stored.flight_test_crew,
      signatory: !!stored.signatory,
      signatory_military: !!stored.signatory_military
    };
  }
});

function toggle() {
  open.value = !open.value;
}

function close() {
  open.value = false;
}

function goToProfile() {
  close();
  router.push("/Home/Profile");
}

function goToNotifications() {
  close();
  router.push("/Home/Notifications");
}

function reportIssue() {
  close();
  addBug({ form: props.form, store });
}

function applyFakeClaims() {
  // Build override object with only truthy claims
  const override = {};
  if (fakeClaims.value.flight_test_crew) override.flight_test_crew = true;
  if (fakeClaims.value.signatory) override.signatory = true;
  if (fakeClaims.value.signatory_military) override.signatory_military = true;
  if (fakeClaims.value.internal) {override.internal = true;} else override.internal = false;
  console.log("Override claims:", override);
  // Set in sessionStorage (or clear if empty)
  if (Object.keys(override).length > 0) {
    setAdminFakeClaims(override);
  } else {
    setAdminFakeClaims(null);
  }
  
  // Reload page to apply new claims
  window.location.reload();
}

function clearFakeClaims() {
  fakeClaims.value = {
    internal: false,
    flight_test_crew: false,
    signatory: false,
    signatory_military: false
  };
  setAdminFakeClaims(null);
  window.location.reload();
}

function handleSignout() {
  close();
  signout(store);
}
</script>
